/**
 * Capture de l'installation PWA.
 *
 * Chrome/Edge (Android + desktop) n'affichent plus de bannière d'installation
 * automatique : sans code de notre part, l'utilisateur ne voit rien. On capte
 * donc `beforeinstallprompt` le plus tôt possible (via initPwaInstall dans
 * main.tsx) pour pouvoir déclencher le vrai dialogue natif depuis notre propre
 * bouton. iOS ne supporte pas cet événement : l'ajout se fait à la main via
 * Safari (Partager -> Sur l'écran d'accueil).
 */

export interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    prompt: () => Promise<void>;
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function emit(): void {
    for (const listener of listeners) listener();
}

/** À appeler une seule fois, au démarrage, avant que React ne monte. */
export function initPwaInstall(): void {
    window.addEventListener('beforeinstallprompt', (event) => {
        // Empêche l'UI native par défaut : on présente notre propre invitation.
        event.preventDefault();
        deferredPrompt = event as BeforeInstallPromptEvent;
        emit();
    });
    window.addEventListener('appinstalled', () => {
        installed = true;
        deferredPrompt = null;
        emit();
    });
}

export function subscribePwaInstall(callback: () => void): () => void {
    listeners.add(callback);
    return () => {
        listeners.delete(callback);
    };
}

export function canPromptInstall(): boolean {
    return deferredPrompt !== null;
}

export function wasInstalled(): boolean {
    return installed;
}

/** Déclenche le dialogue natif d'installation (Android/desktop uniquement). */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!deferredPrompt) return 'unavailable';
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    emit();
    return outcome;
}
